import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate, SIGN_STATUS, ROLE_LABELS } from "@/lib/labels";
import { Eye, Download, FileText, IdCard, PenLine, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const nok = (v) => (v || v === 0) ? `${Number(v).toLocaleString("no-NO")} kr` : "—";

function openDoc(path) {
  api.get(path, { responseType: "blob" }).then((r) => {
    window.open(URL.createObjectURL(r.data), "_blank");
  }).catch((e) => toast.error(apiError(e)));
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-border/40 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right">{value || "—"}</span>
  </div>
);

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.new_password.length < 8) { toast.error("Nytt passord må være minst 8 tegn"); return; }
    if (f.new_password !== f.confirm) { toast.error("Passordene er ikke like"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Passord endret");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="min-side-password">
      <h3 className="font-display text-lg mb-4 flex items-center gap-2"><KeyRound className="w-5 h-5" /> Endre passord</h3>
      <div className="space-y-3 max-w-sm">
        <div><Label className="label-caps">Nåværende passord</Label><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="pw-current" autoComplete="current-password" /></div>
        <div><Label className="label-caps">Nytt passord</Label><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="pw-new" autoComplete="new-password" /></div>
        <div><Label className="label-caps">Bekreft nytt passord</Label><Input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="pw-confirm" autoComplete="new-password" /></div>
        <p className="text-xs text-muted-foreground">Minst 8 tegn.</p>
        <Button onClick={submit} disabled={busy || !f.current_password || !f.new_password} data-testid="pw-save" className="bg-white text-black hover:bg-white/90">{busy ? "Lagrer…" : "Endre passord"}</Button>
      </div>
    </div>
  );
}

export default function MinSide() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const load = () => {
    api.get("/me/employee").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/me/countersign-pending").then((r) => setPending(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const countersign = async (eid) => {
    try {
      const { data: res } = await api.post(`/employment/${eid}/countersign`);
      toast.success(res.completed ? "Arbeidsavtale ferdig signert" : "Signert som arbeidsgiver");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <p className="text-sm text-muted-foreground">Laster…</p>;
  const hr = data.hr || {};
  const contracts = data.contracts || [];

  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <div className="bg-card border border-emerald-500/30 rounded-xl p-6" data-testid="min-side-countersign">
          <h3 className="font-display text-lg mb-1 flex items-center gap-2"><PenLine className="w-5 h-5 text-emerald-400" /> Arbeidsavtaler til din signatur (arbeidsgiver)</h3>
          <p className="text-xs text-muted-foreground mb-4">Den ansatte har signert. Signer som arbeidsgiver for å fullføre avtalen.</p>
          <div className="space-y-3">
            {pending.map((e) => (
              <div key={e.id} className="p-3 rounded-lg bg-secondary/50 border border-border" data-testid={`countersign-pending-${e.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{e.contract_id} <span className="text-xs text-muted-foreground">· {e.user_name}</span></p>
                    <p className="text-xs text-muted-foreground">Ansatt signert{e.employee_signer_name ? `: ${e.employee_signer_name}` : ""}{e.employee_signed_at ? ` · ${fmtDate(e.employee_signed_at)}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openDoc(`/employment/${e.id}/preview`)} className="text-muted-foreground hover:text-foreground p-1" title="Forhåndsvis"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => countersign(e.id)} data-testid={`countersign-btn-${e.id}`} className="inline-flex items-center gap-1 rounded-md bg-white text-black text-xs font-medium px-3 py-1.5 hover:bg-white/90">
                      <PenLine className="w-3.5 h-3.5" /> Signer som arbeidsgiver
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-card border border-border rounded-xl p-6" data-testid="min-side-hr">
        <h3 className="font-display text-lg mb-4 flex items-center gap-2"><IdCard className="w-5 h-5" /> Mine ansattopplysninger</h3>
        <Row label="Navn" value={data.name} />
        <Row label="E-post" value={data.email} />
        <Row label="Rolle" value={ROLE_LABELS[data.role] || data.role} />
        <Row label="Stilling" value={hr.position_title} />
        <Row label="Stillingsbrøk" value={hr.position_pct != null ? `${hr.position_pct} %` : null} />
        <Row label="Årslønn" value={nok(hr.annual_salary)} />
        <Row label="Tiltredelsesdato" value={hr.start_date ? fmtDate(hr.start_date) : null} />
        {data.probation && (
          <Row label="Prøvetid" value={data.probation.applicable === false
            ? "Ingen prøvetid avtalt"
            : data.probation.completed
              ? <span className="text-emerald-400">Gjennomført</span>
              : `Pågår (utløper ${fmtDate(data.probation.end)})`} />
        )}
        <Row label="Feriedager" value={hr.vacation_days} />
        <Row label="Avdeling" value={hr.department || data.department} />
        <Row label="Lokasjon" value={hr.location || data.location} />
        <Row label="Kontonummer" value={hr.bank_account} />
        <Row label="Nødkontakt" value={hr.emergency_contact_name ? `${hr.emergency_contact_name}${hr.emergency_contact_phone ? " · " + hr.emergency_contact_phone : ""}${hr.emergency_contact_relation ? " (" + hr.emergency_contact_relation + ")" : ""}` : null} />
        <p className="text-xs text-muted-foreground mt-3">Opplysninger vedlikeholdes av HR/ledelsen. Ta kontakt ved feil.</p>
      </div>

      <div className="bg-card border border-border rounded-xl p-6" data-testid="min-side-contracts">
        <h3 className="font-display text-lg mb-4 flex items-center gap-2"><FileText className="w-5 h-5" /> Mine arbeidsavtaler</h3>
        {contracts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen arbeidsavtaler registrert ennå.</p>
        ) : (
          <div className="space-y-3">
            {contracts.map((e) => (
              <div key={e.id} className="p-3 rounded-lg bg-secondary/50 border border-border" data-testid={`my-contract-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{e.contract_id} <span className="text-xs text-muted-foreground">· {SIGN_STATUS[e.sign_status] || e.sign_status}</span></p>
                    <p className="text-xs text-muted-foreground">Opprettet {fmtDate(e.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openDoc(`/employment/${e.id}/preview`)} className="text-muted-foreground hover:text-foreground p-1" title="Forhåndsvis"><Eye className="w-4 h-4" /></button>
                    {e.signed && <button onClick={() => openDoc(`/employment/${e.id}/pdf`)} className="text-muted-foreground hover:text-foreground p-1" title="Last ned signert PDF"><Download className="w-4 h-4" /></button>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  <span className={e.employee_signed ? "text-emerald-400" : "text-muted-foreground"}>{e.employee_signed ? "✓" : "○"} Ansatt</span>
                  <span className={e.leader_signed ? "text-emerald-400" : "text-muted-foreground"}>{e.leader_signed ? "✓" : "○"} Arbeidsgiver</span>
                  {e.signed && <span className="text-emerald-400 font-medium">· Ferdig signert</span>}
                </div>
                {!e.employee_signed && ["sendt", "venter_ansatt", "venter_motsignatur", "delvis_signert"].includes(e.sign_status) && e.sign_link_token && (
                  <div className="mt-2 rounded-lg bg-background/60 border border-border p-3">
                    <p className="text-xs text-muted-foreground mb-2">Din arbeidsavtale er klar for signering. Engangskode: <strong className="text-foreground tracking-widest">{e.sign_code}</strong> (også sendt på e-post).</p>
                    <button onClick={() => window.open(`${window.location.origin}/sign/${e.sign_link_token}`, "_blank")} data-testid={`my-sign-${e.id}`} className="inline-flex items-center gap-1 rounded-md bg-white text-black text-xs font-medium px-3 py-1.5 hover:bg-white/90">
                      <PenLine className="w-3.5 h-3.5" /> Signer arbeidsavtalen
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      <ChangePassword />
    </div>
  );
}
